import DashboardLayout from 'components/LayoutContainers/DashboardLayout'
import { Routes, Route } from 'react-router-dom'
import DashboardNavbar from 'components/Navbars/DashboardNavbar'
import dashboardData from 'layouts/authenticated/pages/dashboard/data/dashboardData'
import { IntakeContext } from 'context/IntakeContext'
import { useDashboardCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useDashboardCalculations'
import { selectData } from 'layouts/onboarding'
import { useState, useContext, useEffect } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { apiClient } from 'utils/api/apiClient'
import {
  format,
  addYears,
  addDays,
  addMonths,
  max,
  min,
  differenceInMonths,
} from 'date-fns'
import { Box, Grid, Typography } from '@mui/material'

function createData(name, calculation, explanation, formatAsNumber = true) {
  return { name, calculation, explanation, formatAsNumber }
}

function buildTable(info, tableName) {
  return (
    <TableContainer
      component={Paper}
      sx={{
        maxWidth: 900,
        margin: '1rem auto',
        boxShadow: 0,
        borderRadius: 2,
        border: '1px solid #D2D6DA',
      }}
    >
      <Table size="small" aria-label="calculations table" sx={{}}>
        <TableHead sx={{ display: 'table-header-group' }}>
          <TableRow>
            <TableCell>Calculation Name</TableCell>
            <TableCell>Calculated Value</TableCell>
            <TableCell>Explanation</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {info.map((row) => (
            <TableRow
              key={`${tableName}-${row.key || row.name}`}
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                td: { padding: '6px 16px', fontSize: '0.875rem' },
              }}
            >
              <TableCell>{row.name}</TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.calculation)
                    ? row.calculation
                    : '-'
                  : row.calculation}
              </TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.explanation)
                    ? row.explanation
                    : '-'
                  : row.explanation}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function createCheckData(
  scenario,
  annual,
  monthly,
  percentage,
  explanation,
  formatAsNumber = true
) {
  return { scenario, annual, monthly, percentage, explanation, formatAsNumber }
}

function buildCheckTable(info) {
  return (
    <TableContainer
      component={Paper}
      sx={{
        maxWidth: 900,
        margin: '1rem auto',
        boxShadow: 0,
        borderRadius: 2,
        border: '1px solid #D2D6DA',
      }}
    >
      <Table size="small" aria-label="calculations table" sx={{}}>
        <TableHead sx={{ display: 'table-header-group' }}>
          <TableRow>
            <TableCell>Scenario</TableCell>
            <TableCell>Annual ($)</TableCell>
            <TableCell>Monthly ($)</TableCell>
            <TableCell>Percentage (%)</TableCell>
            <TableCell>Explaination</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {info.map((row) => (
            <TableRow
              key={`checkTable-${row.scenario}`}
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                td: { padding: '6px 16px', fontSize: '0.875rem' },
              }}
            >
              <TableCell>{row.scenario}</TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.annual)
                    ? row.annual
                    : '-'
                  : row.annual}
              </TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.monthly)
                    ? row.monthly
                    : '-'
                  : row.monthly}
              </TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.percentage)
                    ? row.percentage
                    : '-'
                  : row.percentage}
              </TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.explanation)
                    ? row.explanation
                    : '-'
                  : row.explanation}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function createNewData(
  name,
  explanation,
  zero,
  one,
  two,
  three,
  four,
  five,
  key,
  formatAsNumber = true
) {
  return {
    name,
    explanation,
    zero,
    one,
    two,
    three,
    four,
    five,
    key: key || name,
    formatAsNumber,
  }
}

function buildNewCalcTable(info) {
  return (
    <TableContainer
      component={Paper}
      sx={{
        maxWidth: 1600,
        margin: '1rem auto',
        boxShadow: 0,
        borderRadius: 2,
        border: '1px solid #D2D6DA',
      }}
    >
      <Table size="small" aria-label="calculations table" sx={{}}>
        <TableHead sx={{ display: 'table-header-group' }}>
          <TableRow>
            <TableCell>Calculation Name</TableCell>
            <TableCell>Calculation Explanation</TableCell>
            <TableCell>0%</TableCell>
            <TableCell>1%</TableCell>
            <TableCell>2%</TableCell>
            <TableCell>3%</TableCell>
            <TableCell>4%</TableCell>
            <TableCell>5%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {info.map((row) => (
            <TableRow
              key={`newCalcTable-${row.key}`}
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                td: { padding: '6px 16px', fontSize: '0.875rem' },
              }}
            >
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.explanation}</TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.zero)
                    ? row.zero
                    : '-'
                  : row.zero}
              </TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.one)
                    ? row.one
                    : '-'
                  : row.one}
              </TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.two)
                    ? row.two
                    : '-'
                  : row.two}
              </TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.three)
                    ? row.three
                    : '-'
                  : row.three}
              </TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.four)
                    ? row.four
                    : '-'
                  : row.four}
              </TableCell>
              <TableCell>
                {row.formatAsNumber
                  ? !isNaN(row.five)
                    ? row.five
                    : '-'
                  : row.five}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export const Calculations = () => {
  const { intakeState } = useContext(IntakeContext)
  const {
    personalDetailsCurrentAge,
    eligibilityScenariosFirstFullyEligibleDate,
    netIncomeTodayCalculated,
    gapCalculated,
    netIncomeInRetirementCalculated,
    atRetirementGrossIncome,
    eligibilityScenariosModeledCYSinRetirement,
    socialSecurityEstimatedSocialSecurityatFRA,
    socialSecurityEstimatedSocialSecurityatAge62,
    socialSecuritySocialSecurityBenefit,
    FERSYearsOfService,
    atRetirementBasicLife,
    atRetirementIncomeTaxes,
    atRetirementGrossPension,
    atRetirementFERSSupplement,
    atRetirementSocialSecurity,
    atRetirementSSTaxes,
    atRetirementFEHB,
    creditableYearsOfServiceAtRetirement,
  } = useDashboardCalculations()

  const [assets, setAssets] = useState([])
  const [liabilities, setLiabilities] = useState([])

  useEffect(() => {
    let loaded = false
    if (loaded) return

    const getAccounts = async () => {
      const {
        data: { items },
      } = await apiClient.get('api/assets')
      const foundAssets = items?.filter(({ type }) => type === 'asset') || []
      setAssets(foundAssets)
      const foundLiabilities =
        items?.filter(({ type }) => type === 'liability') || []
      setLiabilities(foundLiabilities)
      loaded = true
    }
    getAccounts()
  }, [])

  console.log(assets)

  const clientRetirementAge =
    differenceInMonths(
      new Date(intakeState.dateOfPlannedRetirement),
      new Date(intakeState.dateOfBirth)
    ) / 12

  const dollarFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  const mandatoryRetirementDate =
    selectData.retirementSystem.find(
      ({ value }) => value === intakeState.retirementSystem
    )?.mandatoryRetirementAge === 'Yes'
      ? 'something'
      : 'No Mandatory Retirement Age'

  const earliestFullyEligibleDate =
    new Date(eligibilityScenariosFirstFullyEligibleDate) < new Date()
      ? 'TODAY!'
      : format(eligibilityScenariosFirstFullyEligibleDate, 'MM/dd/yyyy')
  const holder = format(
    eligibilityScenariosFirstFullyEligibleDate,
    'MM/dd/yyyy'
  )

  // Constants
  const govSSTaxAmount = 160200
  const ssTaxPercent = 0.062
  const medicareTaxPercent = 0.0145
  // Create Pension Lookup Table to define
  const pensionContribution = 0.008

  // Little g Calcs
  const ssTaxBaseSalaryAmount = Math.min(
    intakeState.baseSalary * ssTaxPercent,
    govSSTaxAmount * ssTaxPercent
  )
  const medicareTaxBaseSalaryAmount =
    intakeState.baseSalary * medicareTaxPercent
  const pensionContributionAmount = intakeState.baseSalary * pensionContribution
  const tspContributionAmount =
    intakeState.baseSalary * (intakeState.tspContribution / 100)
  const littleGAmount =
    intakeState.baseSalary -
    ssTaxBaseSalaryAmount -
    medicareTaxBaseSalaryAmount -
    pensionContributionAmount -
    tspContributionAmount
  const littleGpercentage =
    ssTaxPercent +
    medicareTaxPercent +
    pensionContribution +
    intakeState.tspContribution / 100

  const getIncomePriceAtIndex = (index) =>
    dashboardData.pricetables[Math.round(personalDetailsCurrentAge)]?.[index] ||
    {}
  const incomePrice0 = getIncomePriceAtIndex(0).male
  const incomePrice1 = getIncomePriceAtIndex(1).male
  const incomePrice2 = getIncomePriceAtIndex(2).male
  const incomePrice3 = getIncomePriceAtIndex(3).male
  const incomePrice4 = getIncomePriceAtIndex(4).male
  const incomePrice5 = getIncomePriceAtIndex(5).male

  const getPayoutRatioAtIndex = (index) =>
    dashboardData.payoutratio[Math.round(personalDetailsCurrentAge)]?.[index] ||
    {}
  const payoutRatio0 = getPayoutRatioAtIndex(0).male
  const payoutRatio1 = getPayoutRatioAtIndex(1).male
  const payoutRatio2 = getPayoutRatioAtIndex(2).male
  const payoutRatio3 = getPayoutRatioAtIndex(3).male
  const payoutRatio4 = getPayoutRatioAtIndex(4).male
  const payoutRatio5 = getPayoutRatioAtIndex(5).male

  const riskFreeRate = 0.04523
  const jobSecurity = 0.999
  const dateDiff =
    -differenceInMonths(
      new Date(intakeState.dateOfPlannedRetirement),
      new Date()
    ) / 12
  const secondHalf = (1 - (1 + riskFreeRate) ** dateDiff) / riskFreeRate
  const capOfWorkingIncome = intakeState.baseSalary * jobSecurity * secondHalf

  const getCapOfPensionAtIndex = (index) =>
    dashboardData.pricetables[Math.round(clientRetirementAge)]?.[index] || {}
  const capOfPension0 =
    atRetirementGrossPension * getCapOfPensionAtIndex(0).male
  const capOfPension1 =
    atRetirementGrossPension * getCapOfPensionAtIndex(1).male
  const capOfPension2 =
    atRetirementGrossPension * getCapOfPensionAtIndex(2).male
  const capOfPension3 =
    atRetirementGrossPension * getCapOfPensionAtIndex(3).male
  const capOfPension4 =
    atRetirementGrossPension * getCapOfPensionAtIndex(4).male
  const capOfPension5 =
    atRetirementGrossPension * getCapOfPensionAtIndex(5).male

  const capOfFERSSupplement = ''

  const capOfSocialSecurity0 =
    atRetirementSocialSecurity *
    dashboardData.pricetables[Math.round(Math.max(62, clientRetirementAge))][0]
      .male
  const capOfSocialSecurity1 =
    atRetirementSocialSecurity *
    dashboardData.pricetables[Math.round(Math.max(62, clientRetirementAge))][1]
      .male
  const capOfSocialSecurity2 =
    atRetirementSocialSecurity *
    dashboardData.pricetables[Math.round(Math.max(62, clientRetirementAge))][2]
      .male
  const capOfSocialSecurity3 =
    atRetirementSocialSecurity *
    dashboardData.pricetables[Math.round(Math.max(62, clientRetirementAge))][3]
      .male
  const capOfSocialSecurity4 =
    atRetirementSocialSecurity *
    dashboardData.pricetables[Math.round(Math.max(62, clientRetirementAge))][4]
      .male
  const capOfSocialSecurity5 =
    atRetirementSocialSecurity *
    dashboardData.pricetables[Math.round(Math.max(62, clientRetirementAge))][5]
      .male

  const subtotalOfCapValues0 =
    capOfWorkingIncome + capOfPension0 + capOfSocialSecurity0

  const subtotalOfCapValues1 =
    capOfWorkingIncome + capOfPension1 + capOfSocialSecurity1

  const subtotalOfCapValues2 =
    capOfWorkingIncome + capOfPension2 + capOfSocialSecurity2

  const subtotalOfCapValues3 =
    capOfWorkingIncome + capOfPension3 + capOfSocialSecurity3

  const subtotalOfCapValues4 =
    capOfWorkingIncome + capOfPension4 + capOfSocialSecurity4

  const subtotalOfCapValues5 =
    capOfWorkingIncome + capOfPension5 + capOfSocialSecurity5

  const capValueOfAssets =
    intakeState?.tspBalance +
    (assets || [])
      .reduce((filtered, option) => {
        if (
          option.subType === 'checkingsavings' ||
          option.subType === 'investment'
        ) {
          var newList = {
            name: option.name,
            amount: option.amount,
          }
          filtered.push(newList)
        }
        return filtered
      }, [])
      .map((assets) => assets.amount)
      .reduce((a, b) => a + b, 0) -
    (liabilities || [])
      .reduce((filtered, option) => {
        if (option.subType === 'creditcard') {
          var newList = {
            name: option.name,
            amount: option.amount,
          }
          filtered.push(newList)
        }
        return filtered
      }, [])
      .map((liabilities) => liabilities.amount)
      .reduce((a, b) => a + b, 0)

  const totalCapValue0 = capValueOfAssets + subtotalOfCapValues0
  const totalCapValue1 = capValueOfAssets + subtotalOfCapValues1
  const totalCapValue2 = capValueOfAssets + subtotalOfCapValues2
  const totalCapValue3 = capValueOfAssets + subtotalOfCapValues3
  const totalCapValue4 = capValueOfAssets + subtotalOfCapValues4
  const totalCapValue5 = capValueOfAssets + subtotalOfCapValues5

  const readinessScore0 = Math.round(
    (totalCapValue0 / (littleGAmount / (1 / incomePrice0))) * 100
  )
  const readinessScore1 = Math.round(
    (totalCapValue1 / (littleGAmount / (1 / incomePrice1))) * 100
  )
  const readinessScore2 = Math.round(
    (totalCapValue2 / (littleGAmount / (1 / incomePrice2))) * 100
  )
  const readinessScore3 = Math.round(
    (totalCapValue3 / (littleGAmount / (1 / incomePrice3))) * 100
  )
  const readinessScore4 = Math.round(
    (totalCapValue4 / (littleGAmount / (1 / incomePrice4))) * 100
  )
  const readinessScore5 = Math.round(
    (totalCapValue5 / (littleGAmount / (1 / incomePrice5))) * 100
  )

  const readinessScoreAverage =
    (readinessScore0 +
      readinessScore1 +
      readinessScore2 +
      readinessScore3 +
      readinessScore4 +
      readinessScore5) /
    6 /
    100

  const readinessScoreAdjustedMax =
    readinessScoreAverage < 0
      ? 0
      : readinessScoreAverage >= 0.95
      ? 0.95
      : readinessScoreAverage

  console.log(intakeState)

  const basicInfoRows = [
    createData(
      'Client First Name',
      intakeState.firstName,
      'User Provided',
      false
    ),
    createData(
      'Client Preferred Name',
      intakeState.firstName,
      'User Provided',
      false
    ),
    createData(
      'Client Last Name',
      intakeState.lastName,
      'User Provided',
      false
    ),
    createData('Email', intakeState.email, 'User Provided', false),
    createData('Phone Number', intakeState.phone, 'User Provided', false),
    createData('Street Address', intakeState.address, 'Unasked', false),
    createData('ZIP Code', intakeState.zipcode, 'User Provided', false),
    createData('City', intakeState.city, 'Lookup Based on Zipcode', false),
    createData(
      'Agency',
      selectData.agency.find(({ value }) => value === intakeState.agency)
        ?.label || '(No agency selected)',
      'User Provided',
      false
    ),
    createData('Position', intakeState.positionTitle, 'User Provided', false),
    createData(
      'Job Description',
      intakeState.jobDescription,
      'Not Asked',
      false
    ),
    createData('State', intakeState.state, 'Lookup Based on Zipcode', false),
    createData(
      'Retirement Strategist',
      intakeState.retirementStrategist,
      'Assigned by PARCO',
      false
    ),
    createData('(RS) Licensed?', 'Yes/No', 'Lookup Based on State', false),
  ]

  const pensionInfoRows = [
    createData(
      'Current Employment Status',
      intakeState.employmentStatus,
      'UserProvided',
      false
    ),
    createData(
      'DOB',
      format(new Date(intakeState.dateOfBirth), 'MM/dd/yyyy'),
      'UserProvided',
      false
    ),
    createData(
      'Current Age',
      personalDetailsCurrentAge,
      'Today - DOB as difference in months / 12',
      false
    ),
    createData(
      'Service Computation Date (SCD)',
      intakeState.dateOfHire,
      'UserProvided',
      false
    ),
    createData(
      'Retirement System',
      selectData.retirementSystem.find(
        ({ value }) => value === intakeState.retirementSystem
      )?.label || '(None selected)',
      'UserProvided',
      false
    ),
    createData('Retirement Type', 'Full', 'Assigned by PARCO', false),
    createData(
      'Mandatory Retirement Date (if Applicable)',
      mandatoryRetirementDate,
      'UserProvided',
      false
    ),
    createData(
      'Earliest Possible Fully Eligible Retirement Date',
      earliestFullyEligibleDate,
      'If less than today we would say "TODAY" Calculated with the earliest date on eligibility scenarios',
      false
    ),
    createData(
      'Expected Retirement Date',
      intakeState.dateOfPlannedRetirement,
      'UserProvided',
      false
    ),
    createData(
      'CSRS Offset Amount (Monthly) $',
      'CSRS Calc',
      'Based on CSRS Scenario',
      false
    ),
    createData(
      'Client Retirement Age (Years & Months)',
      clientRetirementAge,
      'Calc based on Planned Retirement Date and DOB',
      false
    ),
    createData('Client Sex', 'Male', 'Assigned by PARCO'),
  ]

  const ssInfoRows = [
    createData(
      'High Three',
      dollarFormat.format(intakeState.highThree),
      'User Provided',
      false
    ),
    createData(
      'Retirement System',
      intakeState.retirementSystem,
      'User Provided',
      false
    ),
    createData(
      'Age At Retirement',
      differenceInMonths(
        new Date(intakeState.dateOfPlannedRetirement),
        new Date(intakeState.dateOfBirth)
      ) / 12,
      'User Provided',
      false
    ),
    createData(
      'Years of Service at Retirement',
      FERSYearsOfService,
      'User Provided',
      false
    ),
    createData(
      'Creditable Years of Service at Retirement',
      creditableYearsOfServiceAtRetirement,
      'PARCO Calculated based on User Input of Leave',
      false
    ),
    createData('Pension Multiple', 'dashboard-calcs', 'User Provided', false),
    createData(
      'Estimated Social Security at FRA',
      dollarFormat.format(intakeState.estimatedSocialSecurity),
      'User Provided',
      false
    ),
    createData(
      'Calculated Social Security Benefit',
      dollarFormat.format(socialSecuritySocialSecurityBenefit),
      'User Provided',
      false
    ),
    createData(
      'Calculated Social Security at 62',
      dollarFormat.format(socialSecurityEstimatedSocialSecurityatAge62),
      'User Provided',
      false
    ),
    createData(
      'Calcs are close but not on',
      'investigate accuracy SS Benefit and SS @ 62',
      'BUG FIX',
      false
    ),
    createData(
      'Retirement FEHB',
      dollarFormat.format(atRetirementFEHB),
      'User Provided',
      false
    ),
    createData(
      'Retirement Basic Life',
      dollarFormat.format(atRetirementBasicLife),
      'User Provided',
      false
    ),
    createData(
      'Retirement Pension Taxes',
      dollarFormat.format(atRetirementIncomeTaxes),
      'User Provided',
      false
    ),
    createData(
      'Retirement Social Security Taxes',
      dollarFormat.format(atRetirementSSTaxes),
      'User Provided',
      false
    ),
    createData('Simplified Taxes', 'straight 20%', 'BUG FIX', false),
    createData(
      'Net Take Home Per Pay Period',
      dollarFormat.format(intakeState.netTakeHome),
      'User Provided',
      false
    ),
    createData(
      'Retirement Gross Pension',
      dollarFormat.format(atRetirementGrossPension / 12),
      'User Provided',
      false
    ),
    createData(
      'Retirement FERS Supplement',
      dollarFormat.format(atRetirementFERSSupplement),
      'User Provided',
      false
    ),
    createData(
      'Retirement Social Security',
      dollarFormat.format(atRetirementSocialSecurity / 12),
      'User Provided',
      false
    ),
  ]

  const dashboardInfoRows = [
    createData(
      'Net Income Today',
      dollarFormat.format(netIncomeTodayCalculated),
      'Based on User Reported Net take Home * 26 / 12',
      false
    ),
    createData(
      'Net Income in Retirement',
      dollarFormat.format(netIncomeInRetirementCalculated),
      'Net Take Home Monthly = Retirement Gross - Retirement FEHB - Retirement Basic Life - Retirement Taxes (Pension & SS)',
      false
    ),
    createData(
      'Retirement Gross',
      dollarFormat.format(atRetirementGrossIncome),
      'Retirement Gross Pension + Retirement Fers Supplement + Retirement Social Security',
      false
    ),
    createData(
      'Gap',
      dollarFormat.format(gapCalculated),
      'Net Income Today - Net Income in Retirement',
      false
    ),
    createData(
      'TSP Balance',
      dollarFormat.format(intakeState.tspBalance),
      'User Provided',
      false
    ),
  ]

  const noramlizedGrossInfoRows = [
    createCheckData(
      'Current Annual Base Salary',
      dollarFormat.format(intakeState.baseSalary),
      dollarFormat.format(intakeState.baseSalary / 12),
      '100.0%',
      'User Provided Base Salary',
      false
    ),
    createCheckData(
      'Social Security Taxes',
      dollarFormat.format(ssTaxBaseSalaryAmount),
      dollarFormat.format(ssTaxBaseSalaryAmount / 12),
      ssTaxBaseSalaryAmount / intakeState.baseSalary,
      `Min of (Base Salary (${intakeState.baseSalary}) * ssTaxPercent (${ssTaxPercent})) or (Gov SS Tax Amount Const (${govSSTaxAmount}) * ssTaxPercent (${ssTaxPercent}))`,
      false
    ),
    createCheckData(
      'Medicare Taxes',
      dollarFormat.format(medicareTaxBaseSalaryAmount),
      dollarFormat.format(medicareTaxBaseSalaryAmount / 12),
      medicareTaxBaseSalaryAmount / intakeState.baseSalary,
      `Base Salary (${intakeState.baseSalary}) * medicareTaxAmount (${medicareTaxPercent})`,
      false
    ),
    createCheckData(
      'Pension Contributions',
      dollarFormat.format(pensionContributionAmount),
      dollarFormat.format(pensionContributionAmount / 12),
      medicareTaxBaseSalaryAmount / intakeState.baseSalary,
      `Base Salary (${intakeState.baseSalary}) * pensionContributionAmount (${pensionContribution})`,
      false
    ),
    createCheckData(
      'TSP Contributions',
      dollarFormat.format(tspContributionAmount),
      dollarFormat.format(tspContributionAmount / 12),
      intakeState.tspContribution / 100,
      `Base Salary (${intakeState.baseSalary}) * tspContributionAmount (${
        intakeState.tspContribution / 100
      })`,
      false
    ),
    createCheckData(
      'Normalized Gross Working Income - "g"',
      dollarFormat.format(littleGAmount),
      dollarFormat.format(littleGAmount / 12),
      littleGpercentage,
      `Base Salary (${
        intakeState.baseSalary
      }) - (ssTaxBaseSalaryAmount + medicareTaxBaseSalaryAmount + pensionContributionAmount + tspContributionAmount) (${
        ssTaxBaseSalaryAmount +
        medicareTaxBaseSalaryAmount +
        pensionContributionAmount +
        tspContributionAmount
      })`,
      false
    ),
  ]

  const normalizedGrossRetirementIncome =
    atRetirementGrossPension - 0 + socialSecuritySocialSecurityBenefit * 12

  const retirementIncomeInfoRows = [
    createCheckData(
      'Estimated Gross Pension',
      dollarFormat.format(atRetirementGrossPension),
      dollarFormat.format(atRetirementGrossPension / 12),
      '',
      'Based on H3* CYS * Pension Multiple Fnction, Age/DOB, Start Date, Retiremetn Date',
      false
    ),
    createCheckData(
      'Less Survivor Benefit Election',
      dollarFormat.format(0),
      dollarFormat.format(0),
      '',
      'Need a variable to calculate this on',
      false
    ),
    createCheckData(
      'Adjusted Gross Pension',
      dollarFormat.format(atRetirementGrossPension - 0),
      dollarFormat.format((atRetirementGrossPension - 0) / 12),
      (atRetirementGrossPension - 0) / littleGAmount,
      'Gross Pension - Survivor Benefit Election',
      false
    ),
    createCheckData(
      'Estimated Gross Social Security',
      dollarFormat.format(socialSecuritySocialSecurityBenefit * 12),
      dollarFormat.format(socialSecuritySocialSecurityBenefit),
      (socialSecuritySocialSecurityBenefit * 12) / littleGAmount,
      'Based on Social Security Calculator',
      false
    ),
    createCheckData(
      'Normalized Gross Retirement Income',
      dollarFormat.format(normalizedGrossRetirementIncome),
      dollarFormat.format(normalizedGrossRetirementIncome / 12),
      normalizedGrossRetirementIncome / littleGAmount,
      'Gross Pension and Gross SS Summed',
      false
    ),
    createCheckData(
      'Gap (Surplus)',
      dollarFormat.format(littleGAmount - normalizedGrossRetirementIncome),
      dollarFormat.format(
        (littleGAmount - normalizedGrossRetirementIncome) / 12
      ),
      (littleGAmount - normalizedGrossRetirementIncome) / littleGAmount,
      'Simple Gap Calculation',
      false
    ),
  ]

  const newCalcRows = [
    createNewData(
      'Current Age',
      'Difference in Months(Date today - Date of Birth) / 12',
      Math.round(personalDetailsCurrentAge),
      Math.round(personalDetailsCurrentAge),
      Math.round(personalDetailsCurrentAge),
      Math.round(personalDetailsCurrentAge),
      Math.round(personalDetailsCurrentAge),
      Math.round(personalDetailsCurrentAge),
      false
    ),
    createNewData(
      'Client Sex',
      'Explanation',
      'Male',
      'Male',
      'Male',
      'Male',
      'Male',
      'Male',
      false
    ),
    createNewData(
      'Income Price',
      'Lookup for Income Pricing Based on Actual Annuity Data',
      incomePrice0,
      incomePrice1,
      incomePrice2,
      incomePrice3,
      incomePrice4,
      incomePrice5,
      false
    ),
    createNewData(
      'Income Price Percentage',
      'Lookup for Income Pricing Based on Actual Annuity Data',
      1 / incomePrice0,
      1 / incomePrice1,
      1 / incomePrice2,
      1 / incomePrice3,
      1 / incomePrice4,
      1 / incomePrice5,
      false
    ),
    createNewData(
      'Big G Value',
      'Little g / Income Price Percentage',
      dollarFormat.format(littleGAmount / (1 / incomePrice0)),
      dollarFormat.format(littleGAmount / (1 / incomePrice1)),
      dollarFormat.format(littleGAmount / (1 / incomePrice2)),
      dollarFormat.format(littleGAmount / (1 / incomePrice3)),
      dollarFormat.format(littleGAmount / (1 / incomePrice4)),
      dollarFormat.format(littleGAmount / (1 / incomePrice5)),
      false
    ),
  ]

  const newCalcPartsRows = [
    createData(
      'g Value',
      'Base Salary - SS Amount - Medicare - Pension Contribution - TSP Contribution',
      'Calc Parts',
      false
    ),
    createData(
      'Base Salary',
      dollarFormat.format(intakeState.baseSalary),
      'User Provided Base Salary',
      false
    ),
    createData(
      'SS Tax Amount',
      dollarFormat.format(ssTaxBaseSalaryAmount),
      `Min of (Base Salary (${intakeState.baseSalary}) * ssTaxPercent (${ssTaxPercent})) or (Gov SS Tax Amount Const (${govSSTaxAmount}) * ssTaxPercent (${ssTaxPercent}))`,
      false
    ),
    createData(
      'Medicare Tax Amount',
      dollarFormat.format(medicareTaxBaseSalaryAmount),
      `Base Salary (${intakeState.baseSalary}) * medicareTaxAmount (${medicareTaxPercent})`,
      false
    ),
    createData(
      'Pension Contribution Amount',
      dollarFormat.format(pensionContributionAmount),
      `Base Salary (${intakeState.baseSalary}) * pensionContributionAmount (${pensionContribution})`,
      false
    ),
    createData(
      'TSP Contribution Amount',
      dollarFormat.format(tspContributionAmount),
      `User Defined Contribution Amount (${intakeState.tspContribution / 100})`,
      false
    ),
    createData(
      'g value',
      dollarFormat.format(littleGAmount),
      'Base Salary - SS Amount - Medicare - Pension Contribution - TSP Contribution',
      false
    ),
    createData(
      'Income Price',
      'Calc',
      'Lookup for Income Pricing Based on Actual Annuity Data',
      false
    ),
  ]

  const capValuePartsRows = [
    createNewData(
      'Cap Value of Working Income',
      'Explanation',
      dollarFormat.format(capOfWorkingIncome),
      dollarFormat.format(capOfWorkingIncome),
      dollarFormat.format(capOfWorkingIncome),
      dollarFormat.format(capOfWorkingIncome),
      dollarFormat.format(capOfWorkingIncome),
      dollarFormat.format(capOfWorkingIncome),
      false
    ),
    createNewData(
      'Cap Value of Pension',
      'Explanation',
      dollarFormat.format(capOfPension0),
      dollarFormat.format(capOfPension1),
      dollarFormat.format(capOfPension2),
      dollarFormat.format(capOfPension3),
      dollarFormat.format(capOfPension4),
      dollarFormat.format(capOfPension5),
      false
    ),
    // createNewData(
    //   'Cap Value of FERS Supplement',
    //   'Ignore for Now',
    //   'Need Calc',
    //   'Need Calc',
    //   'Need Calc',
    //   'Need Calc',
    //   'Need Calc',
    //   'Need Calc'
    // ),
    createNewData(
      'Cap Value of Social Security',
      'Retirement Social Security * age of turning on Social Security (max of 62 or Retirement Age)',
      dollarFormat.format(capOfSocialSecurity0),
      dollarFormat.format(capOfSocialSecurity1),
      dollarFormat.format(capOfSocialSecurity2),
      dollarFormat.format(capOfSocialSecurity3),
      dollarFormat.format(capOfSocialSecurity4),
      dollarFormat.format(capOfSocialSecurity5),
      false
    ),
    createNewData(
      'Subtotal of cap values',
      'add cap of WI, Pension, SS',
      dollarFormat.format(subtotalOfCapValues0),
      dollarFormat.format(subtotalOfCapValues1),
      dollarFormat.format(subtotalOfCapValues2),
      dollarFormat.format(subtotalOfCapValues3),
      dollarFormat.format(subtotalOfCapValues4),
      dollarFormat.format(subtotalOfCapValues5),
      false
    ),
    createNewData(
      'Investable Assets',
      'Sum of all investables',
      dollarFormat.format(capValueOfAssets),
      dollarFormat.format(capValueOfAssets),
      dollarFormat.format(capValueOfAssets),
      dollarFormat.format(capValueOfAssets),
      dollarFormat.format(capValueOfAssets),
      dollarFormat.format(capValueOfAssets),
      false
    ),
    createNewData(
      'Total Cap Value',
      'Sum of all Caps',
      dollarFormat.format(totalCapValue0),
      dollarFormat.format(totalCapValue1),
      dollarFormat.format(totalCapValue2),
      dollarFormat.format(totalCapValue3),
      dollarFormat.format(totalCapValue4),
      dollarFormat.format(totalCapValue5),
      false
    ),
    createNewData(
      'Score',
      'Retirement Readiness Score',
      readinessScore0,
      readinessScore1,
      readinessScore2,
      readinessScore3,
      readinessScore4,
      readinessScore5,
      false
    ),
    createNewData(
      'Score',
      'Retirement Readiness Score Average',
      readinessScoreAdjustedMax,
      readinessScoreAverage,
      readinessScoreAverage,
      readinessScoreAverage,
      readinessScoreAverage,
      readinessScoreAverage,
      'key-Retirement-Readiness-Score-Average',
      false
    ),
  ]

  const bigGValue0 = littleGAmount / (1 / incomePrice0)
  const bigGValue1 = littleGAmount / (1 / incomePrice1)
  const bigGValue2 = littleGAmount / (1 / incomePrice2)
  const bigGValue3 = littleGAmount / (1 / incomePrice3)
  const bigGValue4 = littleGAmount / (1 / incomePrice4)
  const bigGValue5 = littleGAmount / (1 / incomePrice5)

  const gapSurplusCalc0 = totalCapValue0 - bigGValue0
  const gapSurplusCalc1 = totalCapValue1 - bigGValue1
  const gapSurplusCalc2 = totalCapValue2 - bigGValue2
  const gapSurplusCalc3 = totalCapValue3 - bigGValue3
  const gapSurplusCalc4 = totalCapValue4 - bigGValue4
  const gapSurplusCalc5 = totalCapValue5 - bigGValue5

  const annualGapSurplusCalc0 = gapSurplusCalc0 * (1 / incomePrice0)
  const annualGapSurplusCalc1 = gapSurplusCalc1 * (1 / incomePrice1)
  const annualGapSurplusCalc2 = gapSurplusCalc2 * (1 / incomePrice2)
  const annualGapSurplusCalc3 = gapSurplusCalc3 * (1 / incomePrice3)
  const annualGapSurplusCalc4 = gapSurplusCalc4 * (1 / incomePrice4)
  const annualGapSurplusCalc5 = gapSurplusCalc5 * (1 / incomePrice5)

  const capUpdatedValuePartsRows = [
    createNewData(
      'G%',
      'Lookup Table',
      payoutRatio0,
      payoutRatio1,
      payoutRatio2,
      payoutRatio3,
      payoutRatio4,
      payoutRatio5,
      false
    ),
    createNewData(
      'G Value',
      'little "g" / payout ratio',
      dollarFormat.format(bigGValue0),
      dollarFormat.format(bigGValue1),
      dollarFormat.format(bigGValue2),
      dollarFormat.format(bigGValue3),
      dollarFormat.format(bigGValue4),
      dollarFormat.format(bigGValue5),
      false
    ),

    createNewData(
      'Cap Value of Working Income',
      'Explanation',
      dollarFormat.format(capOfWorkingIncome),
      dollarFormat.format(capOfWorkingIncome),
      dollarFormat.format(capOfWorkingIncome),
      dollarFormat.format(capOfWorkingIncome),
      dollarFormat.format(capOfWorkingIncome),
      dollarFormat.format(capOfWorkingIncome),
      false
    ),
    createNewData(
      'Cap Value of Pension',
      'Explanation',
      dollarFormat.format(capOfPension0),
      dollarFormat.format(capOfPension1),
      dollarFormat.format(capOfPension2),
      dollarFormat.format(capOfPension3),
      dollarFormat.format(capOfPension4),
      dollarFormat.format(capOfPension5),
      false
    ),
    // createNewData(
    //   'Cap Value of FERS Supplement',
    //   'Ignore for Now',
    //   'Need Calc',
    //   'Need Calc',
    //   'Need Calc',
    //   'Need Calc',
    //   'Need Calc',
    //   'Need Calc'
    // ),
    createNewData(
      'Cap Value of Social Security',
      'Retirement Social Security * age of turning on Social Security (max of 62 or Retirement Age)',
      dollarFormat.format(capOfSocialSecurity0),
      dollarFormat.format(capOfSocialSecurity1),
      dollarFormat.format(capOfSocialSecurity2),
      dollarFormat.format(capOfSocialSecurity3),
      dollarFormat.format(capOfSocialSecurity4),
      dollarFormat.format(capOfSocialSecurity5),
      false
    ),
    createNewData(
      'Subtotal of Cap Values',
      'add cap of WI, Pension, SS',
      dollarFormat.format(subtotalOfCapValues0),
      dollarFormat.format(subtotalOfCapValues1),
      dollarFormat.format(subtotalOfCapValues2),
      dollarFormat.format(subtotalOfCapValues3),
      dollarFormat.format(subtotalOfCapValues4),
      dollarFormat.format(subtotalOfCapValues5),
      false
    ),
    createNewData(
      'Investable Assets',
      'Sum of all investables',
      dollarFormat.format(capValueOfAssets),
      dollarFormat.format(capValueOfAssets),
      dollarFormat.format(capValueOfAssets),
      dollarFormat.format(capValueOfAssets),
      dollarFormat.format(capValueOfAssets),
      dollarFormat.format(capValueOfAssets),
      false
    ),
    createNewData(
      'CapValue Totals',
      'Sum of all Caps',
      dollarFormat.format(totalCapValue0),
      dollarFormat.format(totalCapValue1),
      dollarFormat.format(totalCapValue2),
      dollarFormat.format(totalCapValue3),
      dollarFormat.format(totalCapValue4),
      dollarFormat.format(totalCapValue5),
      false
    ),
    createNewData(
      'Gap (Surplus)',
      'Difference in values',
      dollarFormat.format(gapSurplusCalc0),
      dollarFormat.format(gapSurplusCalc1),
      dollarFormat.format(gapSurplusCalc2),
      dollarFormat.format(gapSurplusCalc3),
      dollarFormat.format(gapSurplusCalc4),
      dollarFormat.format(gapSurplusCalc5),
      false
    ),
    createNewData(
      'Gross Financial Preparedness Score',
      'Retirement Readiness Score',
      readinessScore0,
      readinessScore1,
      readinessScore2,
      readinessScore3,
      readinessScore4,
      readinessScore5,
      false
    ),
    createNewData(
      'Average Financial Preparedness Score',
      'Retirement Readiness Score Average',
      readinessScoreAdjustedMax,
      readinessScoreAverage,
      readinessScoreAverage,
      readinessScoreAverage,
      readinessScoreAverage,
      readinessScoreAverage,
      false
    ),
    createNewData(
      'Annual Surplus / Gap',
      'Difference in values',
      dollarFormat.format(annualGapSurplusCalc0),
      dollarFormat.format(annualGapSurplusCalc1),
      dollarFormat.format(annualGapSurplusCalc2),
      dollarFormat.format(annualGapSurplusCalc3),
      dollarFormat.format(annualGapSurplusCalc4),
      dollarFormat.format(annualGapSurplusCalc5),
      false
    ),
    createNewData(
      'Monthly Surplus / Gap',
      'Difference in values',
      dollarFormat.format(annualGapSurplusCalc0 / 12),
      dollarFormat.format(annualGapSurplusCalc1 / 12),
      dollarFormat.format(annualGapSurplusCalc2 / 12),
      dollarFormat.format(annualGapSurplusCalc3 / 12),
      dollarFormat.format(annualGapSurplusCalc4 / 12),
      dollarFormat.format(annualGapSurplusCalc5 / 12),
      false
    ),
  ]

  const optimizationPartsRows = [
    createData('C Values', 'Calc', 'Explanation', false),
    createData('Cap Values', 'Calc', 'Explanation', false),
    createData('Optimized Values', 'Calc', 'Explanation', false),
  ]

  return (
    <DashboardLayout>
      <Box
        px={{
          xs: 4,
          sm: 4,
          md: 4,
          lg: 6,
          xl: 6,
          xxl: 6,
          test: 6,
          xl3: 6,
        }}
        pb={6}
      >
        <Grid item xs={12} mb={2} mt={6}>
          <Typography
            variant="h1"
            fontWeight="600"
            fontSize="28px"
            textAlign="left"
            color="#262627"
          >
            Calculations Testing Page
          </Typography>
        </Grid>
        <Box mt={3}>
          <p>Basic Information</p>
          {buildTable(basicInfoRows, 'basicInfoRows')}
        </Box>
        <Box mt={3}>
          <p>Pension Calculation Information</p>
          {buildTable(pensionInfoRows, 'pensionInfoRows')}
        </Box>
        <Box mt={3}>
          <p>Variables Calculation Information</p>
          {buildTable(ssInfoRows, 'ssInfoRows')}
        </Box>
        <Box mt={3}>
          <p>Gap Calculation Information</p>
          {buildTable(dashboardInfoRows, 'dashboardInfoRows')}
        </Box>
        <Box mt={3}>
          <p>Normalized Gross Scenarios</p>
          {buildCheckTable(noramlizedGrossInfoRows)}
        </Box>
        <Box mt={3}>
          <p>Retirement Income</p>
          {buildCheckTable(retirementIncomeInfoRows)}
        </Box>
        <Box mt={3}>
          <p>New Calculation</p>
          {buildNewCalcTable(newCalcRows)}
        </Box>
        <Box mt={3}>
          <p>New Calculation Parts</p>
          {buildTable(newCalcPartsRows, 'newCalcPartsRows')}
        </Box>
        <Box mt={3}>
          <p>Cap Value Parts</p>
          {buildNewCalcTable(capValuePartsRows)}
        </Box>
        <Box mt={3}>
          <p>New Cap Value Parts</p>
          {buildNewCalcTable(capUpdatedValuePartsRows)}
        </Box>
        <Box mt={3}>
          <p>Optimization Parts</p>
          {buildTable(optimizationPartsRows, 'optimizationPartsRows')}
        </Box>
      </Box>
    </DashboardLayout>
  )
}
